import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { intl } from 'hellospa/components/hs-intl-provider';
import classNames from 'classnames';
import uuid from 'uuid/v4';
import Button from 'common/components/button';
import { notEmpty, unreachable } from 'hellospa/ts-utils';
import * as Dropdown from 'hellospa/foundation/control/dropdown';
import { Field, FieldTypes } from 'hellospa/components/editor/types';
import {
  Rule, Trigger, Action, AllowedTriggerField,
} from 'hellospa/conditional-logic/types';
import { usePreviousValue } from 'hellospa/common/hooks';
import {
  operatorsForFields, isAllowedFieldType,
} from 'hellospa/conditional-logic';
import TriggerOperatorDropdown from './trigger-operator-dropdown';
import TriggerOperatorDropdownExtras from './trigger-operator-dropdown-extras';
import { PendingRule, ValidationMessage, PendingTrigger } from './types';
import validatePendingRule from './validate';
import { editorContext } from '../context';
import styles from './rules-panel.scss';
import rulesPanelImage from './assets/rules-panel-image.svg';
import deleteIcon from './assets/delete-icon.svg';

export { PendingRule, operatorsForFields, isAllowedFieldType };

const messages = defineMessages({
  show: {
    id: '',
    description: 'panel header text conditional logic panel in editor asking user to create a rule for conditional logic feature which "shows" the field if condition met',
    defaultMessage: 'Show',
  },
  hide: {
    id: '',
    description: 'panel header text conditional logic panel in editor asking user to create a rule for conditional logic feature which "hides" the field if condition met',
    defaultMessage: 'Hide',
  },
});

const buildTrigger = (
  triggerType: AllowedTriggerField,
  operator: Trigger['operator'],
  value: string | boolean | number | string[] | null = null,
): Omit<PendingTrigger, 'id'> => {
  const trigger = {} as PendingTrigger;

  switch (operator) {
    case 'is':
    case 'not':
      trigger.operator = operator;
      if (triggerType === FieldTypes.Checkbox) {
        trigger.value = true;
      } else {
        trigger.value = value || '';
      }
      break;
    case 'gt':
    case 'lt':
    case 'has':
    case 'hasNot':
    case 'match':
    case 'any':
    case 'none':
      trigger.operator = operator;
      // for any/none, these need to be an array
      if (triggerType === FieldTypes.Radio || triggerType === FieldTypes.Dropdown) {
        trigger.value = value || [];
      } else {
        trigger.value = value || '';
      }
      break;
    default:
      break;
  }

  return trigger;
};

export function buildNewRule(triggerFields: Field[]): PendingRule {
  if (triggerFields.length === 0) {
    throw new Error('buildNewRule requires an array of fields');
  }

  const signer = triggerFields[0].signer;
  const radioGroup: { [key: string]: boolean } = {};

  const triggers: PendingRule['triggers'] = triggerFields.map((field): null | PendingTrigger => {
    if (!isAllowedFieldType(field.type)) {
      return null;
    }
    const operators = Object.keys(operatorsForFields[field.type]) as Array<Trigger['operator']>;

    if (field.signer !== signer) {
      throw new Error('Rules can\'t be built from mixed signer fields');
    }

    // this is for multiple triggers, if mutliple radio buttons of the same groups
    // are selected, we don't want to treat them as separate fields
    if (field.type === FieldTypes.Radio) {
      if (radioGroup[field.group]) {
        return null;
      }
      radioGroup[field.group] = true;
    }

    return {
      id: field.id,
      ...buildTrigger(
        field.type,
        operators[0],
      ), // build trigger using the first operator,
    };
  }).filter(notEmpty);

  return {
    id: uuid(),
    triggerOperator: 'AND',
    triggers,
    actions: [],
    signer,
  };
}

export function editRule(rule: Rule, allFields: Field[]): PendingRule {
  if (rule.triggers.length === 0) {
    throw new Error('Can\'t edit a field with no triggers');
  }

  const fieldIds = rule.triggers.map((t) => t.id);
  const fields = allFields.filter((f) => fieldIds.includes(f.id));

  const signer = fields[0].signer;

  return Object.assign({ signer }, rule);
}

// visibility properties
const visibilityProperties: {
  [key: string]: {
    value: string,
    property: { hidden: boolean }
  },
} = {
  show: { value: intl.formatMessage(messages.show), property: { hidden: false } },
  hide: { value: intl.formatMessage(messages.hide), property: { hidden: true } },
};

function useValidation(currentRule: PendingRule) {
  const { addBannerMessage, removeBannerMessage } = React.useContext(editorContext);
  const [validationMessage, setValidationMessage] = React.useState<ValidationMessage | undefined>();

  const runValidation = React.useCallback((rule: PendingRule): Rule | undefined => {
    const maybeRule = validatePendingRule(rule);
    const bannerId = 'rules-error';

    if ('message' in maybeRule) {
      // Only change the validation error if it doesn't match
      if (JSON.stringify(maybeRule) !== JSON.stringify(validationMessage)) {
        addBannerMessage(maybeRule.message, bannerId);
        setValidationMessage(maybeRule);
      }
    } else {
      removeBannerMessage(bannerId);
      setValidationMessage(undefined);
      return maybeRule;
    }
    return undefined;
  }, [addBannerMessage, removeBannerMessage, validationMessage]);

  React.useEffect(() => {
    // Only run validation automatically if there is an error being displayed
    if (validationMessage) {
      runValidation(currentRule);
    }
  }, [currentRule, runValidation, validationMessage]);

  return runValidation;
}

type Props = {
  className?: string
}
function RulesPanel({ className }: Props) {
  const {
    cancelRuleEdit, getSelectedFields, saveRule, rules, fields, editingRule,
    updateRule, ruleActionIds, ruleActionUnselectableIds,
  } = React.useContext(editorContext);
  const currentRule = editingRule;
  const setCurrentRule = React.useCallback((cb: (r: PendingRule) => PendingRule) => {
    if (currentRule) {
      updateRule(currentRule.id, cb);
    }
  }, [currentRule, updateRule]);


  if (!editingRule || !currentRule) {
    throw new Error('The rules panel can\'t be rendered without a rule');
  }
  const triggerFields = React.useMemo(() => {
    if (editingRule) {
      const fieldIds = editingRule.triggers.map((t) => t.id);
      return fields.filter((f) => fieldIds.includes(f.id));
    }

    return [];
  }, [editingRule, fields]);

  const selectedFields = getSelectedFields();

  const [visibility, setVisibility] = React.useState(() => {
    if (editingRule != null && editingRule.actions.length > 0) {
      // get the first action's visibility and select that value
      // if it's hidden is true then value is set to "hide"
      return editingRule.actions[0].hidden ? 'hide' : 'show';
    }

    return 'show'; // default visibility
  });
  const buildAction = React.useCallback((field: Field): Action => {
    if ('group' in field && field.group) {
      return {
        type: 'change-group-visibility',
        groupId: field.group,
        hidden: visibility !== 'show',
      };
    } else {
      return {
        type: 'change-field-visibility',
        fieldId: field.id,
        // if set to show, hidden property is false & vice-versa
        hidden: visibility !== 'show',
      };
    }
  }, [visibility]);

  const prevSelectedFields = usePreviousValue(selectedFields);

  React.useEffect(() => {
    // Trying to prevent an infinite loop.
    if (prevSelectedFields === selectedFields) {
      return;
    }

    setCurrentRule((prevState: PendingRule): PendingRule => {
      const signerId = triggerFields[0].signer;

      // build new actions
      const actionFields = selectedFields.filter((field) => {
        return (
          // must be the same signer
          field.signer === signerId
          // Can't already be a trigger or action of the current rule
          && !prevState.triggers.map((t) => t.id).includes(field.id)
          && !prevState.actions.some((a) => {
            if (a.type === 'change-field-visibility') {
              return field.id === a.fieldId;
            } else if (a.type === 'change-group-visibility') {
              return 'group' in field && a.groupId === field.group;
            }
            unreachable(a);
            return false;
          })
          // Can't be an action of another rule
          && !ruleActionIds.includes(field.id)
          // And can't be Unselectable, because it would cause circular rules
          && !ruleActionUnselectableIds.includes(field.id)
        );
      }).reduce((newActions: Action[], field: Field) => {
        if ('group' in field && field.group) {
          const hasGroupAction = newActions.some((a) => {
            if (a.type === 'change-group-visibility') {
              return a.groupId === field.group;
            }
            return false;
          });

          if (!hasGroupAction) {
            newActions.push({
              type: 'change-group-visibility',
              groupId: field.group,
              hidden: visibility !== 'show',
            });
          }
        } else {
          newActions.push({
            type: 'change-field-visibility',
            fieldId: field.id,
            // if set to show, hidden property is false & vice-versa
            hidden: visibility !== 'show',
          });
        }
        // This would just be a map, but the group branch needs to make sure we
        // don't  duplicate `change-group-visibility` actions.
        return newActions;
      }, []);

      return {
        ...prevState,
        actions: [
          ...prevState.actions,
          ...actionFields,
        ],
      };
    });
    // eslint-disable-next-line max-len
  }, [selectedFields, buildAction, rules, triggerFields, fields, setCurrentRule, prevSelectedFields, ruleActionIds, ruleActionUnselectableIds, visibility]);

  const runValidation = useValidation(currentRule);
  const saveCurrentRule = React.useCallback(() => {
    const rule = runValidation(currentRule);
    if (rule) {
      saveRule(rule);
    }
  }, [currentRule, runValidation, saveRule]);

  // trigger operator update handler
  const updateTriggerOperator = React.useCallback(
    (triggerType: AllowedTriggerField, operator: Trigger['operator']) => {
      // update the rules with the operator
      setCurrentRule((prevState) => ({
        ...prevState,
        triggers: prevState.triggers.map((trigger) => {
          return {
            ...trigger,
            ...buildTrigger(triggerType, operator),
          };
        }),
      }));
    },
    [setCurrentRule],
  );

  // delete object from the current rule actions array
  const onActionDelete = React.useCallback((action: Action) => (): void => {
    setCurrentRule((prevState) => ({
      ...prevState,
      actions: prevState.actions.filter((a) => a !== action),
    }));
  }, [setCurrentRule]);

  const onValidate = React.useCallback((
    fieldType: AllowedTriggerField,
    type: Trigger['operator'],
    trigger: PendingTrigger,
    value: string | boolean,
    name: string,
  ): void => {
    setCurrentRule((prevState) => {
      let valueToUpdate: string | boolean | string[];
      const triggerOperator = trigger.operator;
      if (fieldType === FieldTypes.Text && type === 'match') {
        valueToUpdate = value;
      } else if (fieldType === FieldTypes.Dropdown || FieldTypes.Radio) {
        valueToUpdate = value;
        if (type === 'any' || type === 'none') {
          // this is supposed to be an array for these operators
          const selectedTrigger = prevState.triggers.filter((t) => t.id === trigger.id)[0];
          let currentValues = (selectedTrigger.value || []) as string[];
          // if this is true, it was checked, append it to the array
          if (value) {
            // name is the value of the field,
            // because value is the checked/unchecked property
            // check, if it already exists or not before pushing it
            if (!currentValues.includes(name)) {
              currentValues.push(name);
            }
          } else { // unchecked
            currentValues = currentValues.filter((val) => val !== name);
          }

          valueToUpdate = currentValues;
        }
        // validate other dropdown values here?
      } else {
        valueToUpdate = value; // not sure if this should be like this or not yet
      }

      return {
        ...prevState,
        triggers: prevState.triggers.map((trigger) => {
          return {
            ...trigger,
            ...buildTrigger(fieldType, triggerOperator, valueToUpdate),
          };
        }),
      };
    });

  }, [setCurrentRule]);

  // sets to show/hide
  const updateVisibility = React.useCallback((value) => {
    setVisibility(value);

    // update the actions with the property (hidden: true/false)
    setCurrentRule((prevState) => ({
      ...prevState,
      actions: prevState.actions.map((action): Action => {
        return {
          ...action,
          ...visibilityProperties[value].property,
        };
      }),
    }));
  }, [setCurrentRule]);

  const ruleConditions = (
    // Map over the triggers, becuase when we add operators you can have
    // `if Text1 is "cat" OR Text 1 is "dog" ...`
    currentRule.triggers.map((trigger, i) => {
      const triggerOperator = (currentRule.triggers.length > 1
        ? currentRule.triggerOperator
        : null
      );
      return (
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        <TriggerOptions
          key={
            // Using the index is not ideal, but triggers don't have a
            // unique identifier
            i
          }
          trigger={trigger}
          fields={fields}
          triggerOperator={triggerOperator}
          onValidate={onValidate}
          updateTriggerOperator={updateTriggerOperator} />
      );
    }));
  const ruleActions = (
    <Dropdown.Wrapper key="rule-actions" onSelection={updateVisibility} kind="small" >
      <Dropdown.Button data-qa-ref="rules-panel-property-select">
        {visibilityProperties[visibility].value}
      </Dropdown.Button>
      <Dropdown.Menu tag="ul">
        {Object.keys(visibilityProperties).map((visibility) => {
          return (
            <Dropdown.MenuItem key={visibility} value={visibility} tag="li">
              {visibilityProperties[visibility].value}
            </Dropdown.MenuItem>
          );
        })}
      </Dropdown.Menu>
    </Dropdown.Wrapper>
  );

  return (
    <div className={classNames(styles.panel, className)}>
      <div className={styles.panelHeader}>
        <div>
          <FormattedMessage
            id=""
            description="panel header text conditional logic panel in editor asking user to create a rule for conditional logic feature"
            defaultMessage="Create a rule" />
        </div>

        <div className={styles.right}>
          <React.Fragment>
            <div className={styles.save} >
              <Button
                data-qa-ref="rules-panel-done"
                buttonColor="velvet-evening"
                buttonTextColor="white"
                buttonTextUppercase={false}
                onClick={saveCurrentRule}
                buttonInner={
                  <FormattedMessage
                    id=""
                    description="button text in conditional logic panel in editor when clicked saved the rule for conditional logic"
                    defaultMessage="Done" />
                } />
            </div>

            <div
              onClick={cancelRuleEdit}
              className={styles.cancel}
              data-qa-ref="rules-panel-cancel"
            >
              <FormattedMessage
                id=""
                description="button text in conditional logic panel in editor when clicked closes the conditonal logic panel"
                defaultMessage="Cancel" />
            </div>
          </React.Fragment>
        </div>

      </div>

      <div className={styles.panelBody}>
        <FormattedMessage
          id=""
          description="text in conditional logic panel in editor, simpliy stating if the following 'condition' is met then 'show/hide' the below field that users will populate"
          defaultMessage="<div1>If the following is true...</div1><div2>{condition}then<div3>{action}</div3>the following field(s)</div2>"
          values={{
            // eslint-disable-next-line react/display-name
            div1: (...chunks: any[]) => <div key="div1" className={styles.panelHeading}>{chunks}</div>,
            // eslint-disable-next-line react/display-name
            div2: (...chunks: any[]) => <div key="div2" className={styles.panelFieldInfo}>{chunks}</div>,
            // eslint-disable-next-line react/display-name
            div3: (...chunks: any[]) => <div key="div3" className={styles.inlineDropdown}>{chunks}</div>,
            condition: ruleConditions,
            action: ruleActions,
          }} />
        <div className={styles.panelActions}>
          {currentRule.actions && currentRule.actions.length > 0
            ? currentRule.actions
              .map((action) => {
                if (action.type === 'change-group-visibility') {
                  return fields
                    .filter((f) => 'group' in f && f.group === action.groupId)
                    .map((field) => (
                      // eslint-disable-next-line @typescript-eslint/no-use-before-define
                      <ActionFieldUI
                        key={action.type + field.id}
                        onClick={onActionDelete(action)}
                        field={field} />
                    ));
                } else if (action.type === 'change-field-visibility') {
                  const field = fields.find((field) => field.id === action.fieldId);
                  if (!field) {
                    throw new Error(`Unable to locate field: ${action.fieldId}`);
                  }

                  return (
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    <ActionFieldUI
                      key={action.type + field.id}
                      onClick={onActionDelete(action)}
                      field={field} />
                  );
                }
                unreachable(action);
                return null;
              })
            : <div className={styles.panelImage}>
              <img src={rulesPanelImage} />
              <FormattedMessage
                id=""
                description="text in conditional logic panel in editor asking user to select field from document to load conditional rule in a panel"
                tagName="p"
                defaultMessage="Select a field from your document" />
            </div>
          }
        </div>
      </div>
    </div>
  );
}

type ActionProps = {
  field: Field,
  onClick: () => any
}
function ActionFieldUI({ field, onClick }: ActionProps) {
  return (
    <div className={styles.action} >
      <div
        className={styles.actionLabel}
        key={field.id}
      >
        {field.name || field.id}
      </div>
      <a
        onClick={onClick}
        data-qa-ref="delete-single-action"
        key={`${field.id}-delete`}
      >
        <img src={deleteIcon} />
      </a>
    </div>
  );
}

type TriggerOptionsProps = {
  trigger: PendingTrigger,
  triggerOperator: string | null,
  fields: Field[],
  onValidate: (
    triggerType: AllowedTriggerField,
    type: Trigger['operator'],
    trigger: PendingTrigger,
    value: any,
    name: any
  ) => void,
  updateTriggerOperator: (triggerType: AllowedTriggerField, operator: Trigger['operator']) => void,
}
function TriggerOptions({
  trigger,
  fields,
  triggerOperator,
  onValidate,
  updateTriggerOperator,
}: TriggerOptionsProps) {
  const triggerField = fields.find((f) => f.id === trigger.id);
  if (!triggerField) {
    throw new Error(`Invalid trigger: unable to find field: ${trigger.id}`);
  }

  let triggerName = triggerField.name || triggerField.id;
  if (triggerField.type === FieldTypes.Radio) {
    triggerName = triggerField.groupLabel || triggerName;
  }

  return (
    <React.Fragment key={triggerField.id} >
      <div className={styles.panelFieldTriggerDropdownBlock} data-qa-ref="rules-panel">
        <div className={styles.panelFieldInfoLabel}>
          {triggerName}
        </div>
        <div className={classNames(
          styles.panelFieldOptions,
          {
            [styles.flexDirectionColumn]:
              ['any', 'none'].includes(trigger.operator),
          },
        )}
        >
          <TriggerOperatorDropdown
            triggerField={triggerField}
            onChange={updateTriggerOperator}
            trigger={trigger} />
          <TriggerOperatorDropdownExtras
            triggerField={triggerField}
            onValidate={onValidate}
            trigger={trigger}
            allFields={fields} />
        </div>
      </div>
      {triggerOperator && (
        <p style={{ textAlign: 'center', fontWeight: 700 }}>
          {triggerOperator}
        </p>
      )}
    </React.Fragment>
  );
}

export default React.memo(RulesPanel);
